"use client";
import {
  EmailRegex,
  Login,
  NonEmptyStringRegex,
  PhoneRegex
} from "@rinastyling/common";
import { useRouter } from "next/navigation";
import {
  Dispatch,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import useInput from "../hooks/useInput";
import useRequiredFields from "../hooks/useRequiredFields";
import { AuthService } from "../services/AuthService";
import { handleFetchError } from "../utils/clientErrorHandeling";
import { Routes } from "../utils/clientRoutes";
import TitleWithBorder from "./TitleWithBorder";
import { Button } from "./core/Button";

interface RegisterFormProps {
  setFinishedRegistration?: Dispatch<SetStateAction<Login["request"] | null>>;
}
const RegisterForm = ({ setFinishedRegistration }: RegisterFormProps) => {
  const [isPasswrodAgainErroring, setIsPasswrodAgainErroring] = useState(false);
  const emailInput = useInput({
    label: "אימייל",
    initalValue: "",
    regex: EmailRegex
  });
  const phoneInput = useInput({
    label: "טלפון",
    initalValue: "",
    type: "text",
    regex: PhoneRegex
  });
  const passwordInput = useInput({
    label: "סיסמא",
    initalValue: "",
    type: "text",
    regex: NonEmptyStringRegex
  });
  const passwordAgainInput = useInput({
    label: "סיסמא בשנית",
    initalValue: "",
    type: "text",
    isError: isPasswrodAgainErroring,
    regex: NonEmptyStringRegex
  });
  const firstNameInput = useInput({
    label: "שם פרטי",
    initalValue: "",
    regex: NonEmptyStringRegex
  });
  const lastNameInput = useInput({
    label: "שם משפחה",
    initalValue: "",
    regex: NonEmptyStringRegex
  });
  const authService = useRef(new AuthService());

  const reqFields = useRequiredFields([
    emailInput,
    passwordInput,
    passwordAgainInput,
    firstNameInput,
    lastNameInput
  ]);

  const disabled = useMemo(
    () => reqFields || passwordInput.value !== passwordAgainInput.value,
    [passwordAgainInput.value, passwordInput.value, reqFields]
  );

  useEffect(() => {
    setIsPasswrodAgainErroring(
      passwordInput.value !== passwordAgainInput.value
    );
  }, [passwordAgainInput.value, passwordInput.value]);

  const router = useRouter();
  const onSubmitForm = useCallback(async () => {
    try {
      const res = await authService.current.register({
        email: emailInput.value,
        firstName: firstNameInput.value,
        lastName: lastNameInput.value,
        password: passwordInput.value,
        phone: +phoneInput.value
      });
      toast(res.msg);
      if (!setFinishedRegistration) {
        router.push(Routes.login);
      }
      setFinishedRegistration?.({
        email: emailInput.value,
        password: passwordInput.value
      });
    } catch (error) {
      handleFetchError(error);
    }
  }, [
    emailInput.value,
    firstNameInput.value,
    lastNameInput.value,
    passwordInput.value,
    phoneInput.value,
    router,
    setFinishedRegistration
  ]);

  return (
    <Suspense>
      <RegisterFormInnerWrapper>
        <TitleWithBorder text="הרשמה" />

        {emailInput.jsx}
        {phoneInput.jsx}

        {passwordInput.jsx}
        {passwordAgainInput.jsx}

        {firstNameInput.jsx}
        {lastNameInput.jsx}

        <Button onClick={onSubmitForm} disabled={disabled}>
          הירשם
        </Button>
      </RegisterFormInnerWrapper>
    </Suspense>
  );
};

const RegisterFormInnerWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  gap: 1rem;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.5rem;
`;

export default RegisterForm;
